// Authentication Context for Funstay
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { authAPI } from '../services/api';
import { getTokenExpiryMs } from '../utils/jwt';
import { handleAuthInvalidation, AUTH_REASON } from '../utils/tokenStale';
import { permissionCandidates } from '../agency/data/canonicalPermissionAliases';
import { buildPavfDiagnosticsSnapshot, publishPavfDiagnosticsToWindow } from '../agency/lib/pavfDiagnostics';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const storedUser = authAPI.getCurrentUser();
    if (storedUser) {
      setUser(storedUser);
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    setUser(response.user);
    return response;
  };

  const register = async (userData) => {
    const response = await authAPI.register(userData);
    setUser(response.user);
    return response;
  };

  const logout = () => {
    authAPI.logout();
    setUser(null);
  };

  const updateProfile = async (data) => {
    const response = await authAPI.updateProfile(data);
    setUser(response);
    localStorage.setItem('funstay_user', JSON.stringify(response));
    return response;
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Admin Auth Context
const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [roleName, setRoleName] = useState('');
  const [primaryPosition, setPrimaryPosition] = useState(null);
  const [primaryPositionLabel, setPrimaryPositionLabel] = useState('');
  const [moduleAccessKeys, setModuleAccessKeys] = useState([]);
  const [accessModelVersion, setAccessModelVersion] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const warnTimerRef = useRef(null);
  const expiryTimerRef = useRef(null);

  // ── Proactive session-expiry monitor ────────────────────────────────
  // Decode the JWT `exp` and schedule: (1) a non-intrusive warning ~2 min
  // before expiry, (2) an automatic transition to the "Session Expired"
  // flow AT expiry — so we never wait for the next API call to 401.
  const clearSessionTimers = () => {
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (expiryTimerRef.current) { clearTimeout(expiryTimerRef.current); expiryTimerRef.current = null; }
  };

  const hydrateAdminRuntime = (nextAdmin = null) => {
    const resolvedAdmin = nextAdmin || authAPI.getCurrentAdmin();
    setAdmin(resolvedAdmin);

    const fallbackPermissions = (() => {
      try {
        return JSON.parse(localStorage.getItem('funstay_admin_permissions') || '[]');
      } catch {
        return [];
      }
    })();

    const nextPermissions = Array.isArray(resolvedAdmin?.permissions) && resolvedAdmin.permissions.length
      ? resolvedAdmin.permissions
      : fallbackPermissions;
    const nextPrimaryPosition = resolvedAdmin?.primary_position || null;
    const nextPrimaryPositionLabel = resolvedAdmin?.primary_position_label
      || resolvedAdmin?.primary_position?.label
      || localStorage.getItem('funstay_admin_primary_position_label')
      || '';
    const nextModuleAccessKeys = Array.isArray(resolvedAdmin?.module_access_keys)
      ? resolvedAdmin.module_access_keys
      : Array.isArray(resolvedAdmin?.primary_position?.module_access_keys)
        ? resolvedAdmin.primary_position.module_access_keys
        : [];
    const nextAccessModelVersion = resolvedAdmin?.access_model_version || '';
    const nextRoleName = nextPrimaryPositionLabel || resolvedAdmin?.role_name || '';
    const nextIsSuperAdmin = typeof resolvedAdmin?.is_super_admin === 'boolean'
      ? resolvedAdmin.is_super_admin
      : localStorage.getItem('funstay_admin_is_super_admin') === 'true';

    setPermissions(nextPermissions);
    setPrimaryPosition(nextPrimaryPosition);
    setPrimaryPositionLabel(nextPrimaryPositionLabel);
    setModuleAccessKeys(nextModuleAccessKeys);
    setAccessModelVersion(nextAccessModelVersion);
    setRoleName(nextRoleName);
    setIsSuperAdmin(nextIsSuperAdmin);

    return resolvedAdmin;
  };

  const profileBootstrapRef = useRef(null);

  const loadAuthoritativeAdminProfile = async () => {
    if (profileBootstrapRef.current) return profileBootstrapRef.current;
    profileBootstrapRef.current = authAPI.getAdminProfile()
      .then((freshAdmin) => {
        hydrateAdminRuntime(freshAdmin);
        return freshAdmin;
      })
      .finally(() => {
        profileBootstrapRef.current = null;
      });
    return profileBootstrapRef.current;
  };

  const scheduleSessionTimers = () => {
    clearSessionTimers();
    const token = localStorage.getItem('funstay_admin_token');
    const expMs = getTokenExpiryMs(token);
    if (!expMs) return;
    const now = Date.now();
    const msToExpiry = expMs - now;
    if (msToExpiry <= 0) {
      handleAuthInvalidation(AUTH_REASON.EXPIRED);
      return;
    }
    const WARN_BEFORE = 2 * 60 * 1000; // 2 minutes
    const msToWarn = msToExpiry - WARN_BEFORE;
    if (msToWarn > 0) {
      warnTimerRef.current = setTimeout(() => {
        toast.warning('Your session will expire in 2 minutes.', {
          description: 'Please save your work — you will need to sign in again.',
          duration: 15000,
        });
      }, msToWarn);
    } else {
      toast.warning('Your session will expire soon.', {
        description: 'Please save your work — you will need to sign in again.',
        duration: 15000,
      });
    }
    expiryTimerRef.current = setTimeout(() => {
      handleAuthInvalidation(AUTH_REASON.EXPIRED);
    }, msToExpiry);
  };

  useEffect(() => {
    const storedAdmin = hydrateAdminRuntime();
    const bootstrap = async () => {
      if (localStorage.getItem('funstay_admin_token')) {
        try {
          await loadAuthoritativeAdminProfile();
          scheduleSessionTimers();
        } catch {
          hydrateAdminRuntime(storedAdmin || null);
        }
      } else if (storedAdmin) {
        scheduleSessionTimers();
      }
      setLoading(false);
    };
    bootstrap();
    return () => clearSessionTimers();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.adminLogin(email, password);
    hydrateAdminRuntime(response.user);
    scheduleSessionTimers();
    return response;
  };

  const logout = () => {
    clearSessionTimers();
    authAPI.adminLogout();
    setAdmin(null);
    setPermissions([]);
    setRoleName('');
    setPrimaryPosition(null);
    setPrimaryPositionLabel('');
    setModuleAccessKeys([]);
    setAccessModelVersion('');
    setIsSuperAdmin(false);
  };

  // Apply a partial patch to the current admin (e.g. avatar change) and
  // persist to localStorage so the header/menu reflect it instantly.
  const updateAdmin = (patch) => {
    setAdmin(prev => {
      const next = { ...(prev || {}), ...patch };
      localStorage.setItem('funstay_admin_user', JSON.stringify(next));
      return next;
    });
  };

  const hasPermission = (module, action) => {
    if (!module) return true;
    // Super Admin bypass — mirrors backend `require_permission` semantics.
    if (isSuperAdmin) return true;
    const candidates = permissionCandidates(module, action);
    if (candidates.some((candidate) => permissions.includes(candidate))) return true;
    // Phase 1B — parent dual-acceptance: `settings.users:view` is satisfied
    // by either the literal key OR the legacy parent `settings:view`.
    if (module.includes('.')) {
      const parentKey = `${module.split('.')[0]}:${action}`;
      if (permissions.includes(parentKey)) return true;
    }
    return false;
  };
  const canView = (module) => hasPermission(module, 'view');
  const canCreate = (module) => hasPermission(module, 'create');
  const canEdit = (module) => hasPermission(module, 'edit');
  const canDelete = (module) => hasPermission(module, 'delete');

  useEffect(() => {
    publishPavfDiagnosticsToWindow(buildPavfDiagnosticsSnapshot({
      admin,
      permissions,
      moduleAccessKeys,
      canView,
    }));
  }, [admin, permissions, moduleAccessKeys]);

  const value = {
    admin,
    loading,
    isAuthenticated: !!admin,
    permissions,
    roleName,
    primaryPosition,
    primaryPositionLabel,
    moduleAccessKeys,
    accessModelVersion,
    isSuperAdmin,
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    login,
    logout,
    updateAdmin,
    refreshProfile: loadAuthoritativeAdminProfile,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export default AuthContext;
